import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser'
import QRCode from 'qrcode'
import { KeyRound, Smartphone } from 'lucide-react'
import { Button, Input, Label } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { CopyButton } from '@/components/status'
import { ApiError, post } from '@/lib/api'
import { qk } from '@/lib/query'
import { AuthCard, FormError } from './AuthCard'
import { OtpInput } from './OtpInput'
import { useMe } from './useMe'

type EnrolResult = { recoveryCodes?: string[] | null }

/**
 * 2FA enrolment (03 §5.3). Forced on first login — no other route is reachable
 * until one factor is proved — and reused from /account to add another.
 */
export function EnrolPage() {
  const me = useMe().data
  const nav = useNavigate()
  const qc = useQueryClient()
  const [codes, setCodes] = useState<string[] | null>(null)

  async function finish() {
    await qc.invalidateQueries({ queryKey: qk.me() })
    await nav({ to: '/' })
  }

  if (codes) return <RecoveryCodes codes={codes} onDone={finish} />

  return (
    <AuthCard title="Set up two-factor">
      <p className="mb-4 text-xs text-fg-subtle">Required for everyone. Pick at least one; you can add the other later from your account.</p>
      <EnrolFactors
        onEnrolled={(r) => {
          if (r.recoveryCodes?.length) setCodes(r.recoveryCodes)
          else void finish()
        }}
        hasTotp={!!me?.factors.totp}
      />
    </AuthCard>
  )
}

export function EnrolFactors({ onEnrolled, hasTotp }: { onEnrolled: (r: EnrolResult) => void; hasTotp?: boolean }) {
  const [step, setStep] = useState<'pick' | 'totp' | 'passkey'>('pick')
  if (step === 'totp') return <TotpStep onDone={onEnrolled} onBack={() => setStep('pick')} />
  if (step === 'passkey') return <PasskeyStep onDone={onEnrolled} onBack={() => setStep('pick')} />
  return (
    <div className="grid grid-cols-2 gap-2">
      <SButton variant="outline" disabled={hasTotp} onClick={() => setStep('totp')} className="h-auto flex-col items-start gap-0 whitespace-normal border-border-strong bg-surface-2 p-3 text-left font-normal hover:border-accent hover:bg-hover">
        <Smartphone size={16} className="mb-2 text-fg-subtle" />
        <div className="text-sm font-medium">Authenticator app</div>
        <div className="mt-1 text-2xs text-fg-subtle">6-digit codes from 1Password, Google Authenticator…</div>
        {hasTotp && <div className="mt-1 text-2xs text-ok">Already set up</div>}
      </SButton>
      <SButton variant="outline" onClick={() => setStep('passkey')} className="h-auto flex-col items-start gap-0 whitespace-normal border-border-strong bg-surface-2 p-3 text-left font-normal hover:border-accent hover:bg-hover">
        <KeyRound size={16} className="mb-2 text-fg-subtle" />
        <div className="text-sm font-medium">Passkey</div>
        <div className="mt-1 text-2xs text-fg-subtle">Touch ID, Windows Hello or a security key.</div>
      </SButton>
    </div>
  )
}

function TotpStep({ onDone, onBack }: { onDone: (r: EnrolResult) => void; onBack: () => void }) {
  const [secret, setSecret] = useState<{ secret: string; otpauthUrl: string } | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    post<{ secret: string; otpauthUrl: string }>('/auth/2fa/totp/enroll')
      .then(async (s) => {
        setSecret(s)
        // Rendered locally: the secret never goes to a QR service.
        setQr(await QRCode.toDataURL(s.otpauthUrl, { margin: 1, width: 160, color: { dark: '#f7f8f8', light: '#0f1011' } }))
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? (e.what ?? e.message) : 'Could not start setup.'))
  }, [])

  async function confirm(c: string) {
    setBusy(true)
    setError(null)
    try {
      onDone(await post<EnrolResult>('/auth/2fa/totp/confirm', { code: c }))
    } catch {
      setError("That code didn't match. Check the time on your phone and try the current code.")
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <FormError>{error}</FormError>
      <p className="mb-3 text-xs text-fg-subtle">Scan this with your authenticator app, or enter the secret by hand.</p>
      <div className="mb-3 flex items-center gap-3">
        <div className="size-40 shrink-0 rounded-md border border-border bg-surface-2">{qr && <img src={qr} alt="TOTP QR code" className="size-full" />}</div>
        {secret && (
          <div className="min-w-0">
            <div className="font-mono text-xs break-all text-fg-muted">{secret.secret.replace(/(.{4})/g, '$1 ').trim()}</div>
            <CopyButton text={secret.secret} />
          </div>
        )}
      </div>
      <Label>Enter a code to confirm</Label>
      <OtpInput value={code} onChange={setCode} onComplete={confirm} disabled={busy || !secret} />
      <SButton variant="link" className="mt-4 h-auto self-start p-0 text-xs font-normal text-fg-subtle hover:text-fg" onClick={onBack}>
        Back
      </SButton>
    </div>
  )
}

function PasskeyStep({ onDone, onBack }: { onDone: (r: EnrolResult) => void; onBack: () => void }) {
  const [label, setLabel] = useState(defaultLabel())
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function create() {
    setBusy(true)
    setError(null)
    try {
      const { challengeId, options } = await post<{ challengeId: string; options: PublicKeyCredentialCreationOptionsJSON }>('/auth/2fa/passkey/register/options', {})
      const response = await startRegistration({ optionsJSON: options })
      onDone(await post<EnrolResult>('/auth/2fa/passkey/register/verify', { challengeId, label, response }))
    } catch (e) {
      setError(e instanceof Error && e.name === 'NotAllowedError' ? 'The passkey prompt was cancelled or timed out.' : 'Could not create the passkey. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <FormError>{error}</FormError>
      <Label htmlFor="pk-label">Name</Label>
      <Input id="pk-label" value={label} onChange={(e) => setLabel(e.target.value)} />
      <Button variant="primary" size="md" className="mt-3 w-full" disabled={busy || !label.trim()} onClick={create}>
        <KeyRound size={14} /> Create passkey
      </Button>
      <SButton variant="link" className="mt-4 h-auto self-start p-0 text-xs font-normal text-fg-subtle hover:text-fg" onClick={onBack}>
        Back
      </SButton>
    </div>
  )
}

function defaultLabel(): string {
  const ua = navigator.userAgent
  if (/Mac/.test(ua)) return 'Mac Touch ID'
  if (/Windows/.test(ua)) return 'Windows Hello'
  if (/iPhone|iPad/.test(ua)) return 'iPhone Face ID'
  return 'Passkey'
}

/** Shown once, at first enrolment (01 §3.2). Finishing requires saying they are saved. */
export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false)
  const text = codes.join('\n')
  return (
    <AuthCard title="Recovery codes">
      <p className="mb-3 text-xs text-fg-subtle">If you lose your phone or passkey, each of these signs you in once. They are shown only now.</p>
      <div className="mb-3 grid grid-cols-2 gap-1 rounded-md border border-border bg-surface-2 p-3 font-mono text-xs">
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <div className="mb-3 flex gap-1">
        <CopyButton text={text} />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            const a = document.createElement('a')
            a.href = URL.createObjectURL(new Blob([`One Deployment recovery codes\n\n${text}\n`], { type: 'text/plain' }))
            a.download = 'trisof-recovery-codes.txt'
            a.click()
            URL.revokeObjectURL(a.href)
          }}
        >
          Download .txt
        </Button>
      </div>
      <label className="mb-3 flex items-center gap-2 text-xs">
        <Checkbox checked={saved} onCheckedChange={(v) => setSaved(v === true)} /> I have saved these
      </label>
      <Button variant="primary" size="md" className="w-full" disabled={!saved} onClick={onDone}>
        Finish
      </Button>
    </AuthCard>
  )
}
